import React, { useEffect, useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';

interface PortalProps {
  children: React.ReactNode;
  targetId?: string;
}

// Generic Portal component that renders into a specified container
const Portal: React.FC<PortalProps> = ({ children, targetId = 'dropdown-portal' }) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  if (!mounted) return null;

  const portalContainer = document.getElementById(targetId);
  if (!portalContainer) return null;

  return createPortal(children, portalContainer);
};

// Hook to calculate position for the dropdown
const useDropdownPosition = (triggerRef: React.RefObject<HTMLElement>) => {
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });

  const updatePosition = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      console.log("Bounding rect:", rect); // Log rect values for debugging
      
      // Calculate top, left based on rect alone
      setPosition({
        top: rect.bottom, // Use rect.bottom directly
        left: rect.left,  // Use rect.left directly
        width: rect.width
      });
      
      console.log("Calculated position:", { top: rect.bottom, left: rect.left, width: rect.width });
    }
  }, [triggerRef]);

  useEffect(() => {
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [updatePosition]);

  return position;
};

interface DropdownPortalProps {
  children: React.ReactNode;
  isOpen: boolean;
  triggerRef: React.RefObject<HTMLElement>;
  onClose?: () => void;
}

// Main DropdownPortal component
export const DropdownPortal: React.FC<DropdownPortalProps> = ({
  children,
  isOpen,
  triggerRef,
  onClose
}) => {
  const position = useDropdownPosition(triggerRef);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        onClose?.();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, triggerRef]);

  if (!isOpen) return null;

  return (
    <Portal>
      <div
        ref={dropdownRef}
        className="position-fixed shadow-lg bg-white rounded-bottom border"
        style={{
          top: `${position.top}px`,
          left: `${position.left}px`,
          width: `${position.width}px`,
          zIndex: 9999,
        }}
      >
        {children}
      </div>
    </Portal>
  );
};

export default DropdownPortal;
